import * as React from "react"
import { progress } from "../home/motion"
import { AWARD_FOOT, AWARD_TALL, awardTall } from "./AwardVideo"

/** After the video: four strengths and, at their centre, futurism (Figma "Frame
 *  976", node 19:15), built up as the screen goes dark. The dots come out of the
 *  centre (appearing there, then going straight out to their places), each
 *  text rises in line by line, the lines between the dots draw, and futurism
 *  comes last, its ring around it. Then everything floats a little, and leans
 *  towards the mouse when it comes near. The layout scales with the screen; the
 *  text keeps its size. */

type Point = { id: string; title: string; text: string; /** phones: shorter */ short: string; /** the text's width (px) */ width: number }

const POINTS: Point[] = [
    { id: "product", title: "Product design", text: "end to end product design and animation for established and emerging interfaces.", short: "end to end product design and animation for all interfaces.", width: 233 },
    { id: "delivery", title: "delivery", text: "from design to vibe code delivery, the possibilities are endless and we can make it happen, together.", short: "from design to vibe code delivery, the possibilities are endless.", width: 262 },
    { id: "creative", title: "creative leadership", text: "supporting creative teams to design at their best while developing strong connections.", short: "teams that design at their best while developing strong connections.", width: 252 },
    { id: "brand", title: "brand", text: "brand designer at heart, working on the essence is where I’m home.", short: "brand designer at heart, working on the essence is where I’m home.", width: 237 },
    { id: "futurism", title: "futurism", text: "creating the unseen lies at the very core of what drives me.", short: "creating the unseen lies at the very core of what drives me.", width: 181 },
]
const CENTRE = 4 // futurism
/** the lines: product → delivery → creative → brand → product */
const LOOP = [0, 1, 2, 3]

/** Desktop, from the 1680x788 artboard: each dot's centre and where its title
 *  starts from it (as placed in the design) */
const WIDE = {
    w: 1680,
    h: 788,
    dots: [
        [881.5, 220.5],
        [1089.5, 464.5],
        [692.5, 547.5],
        [425.5, 409.5],
        [785.5, 385.5],
    ],
    titles: [
        [12.5, -8.5],
        [27.5, -9.5],
        [9.5, -9.5],
        [17.5, -7.5],
        [14.5, -10.5],
    ],
}
/** the smallest the spread arrangement may be (of the artboard): smaller, the
 *  texts would overlap (measured from the design's positions and text sizes) */
const MIN_SPREAD = 0.8
/** how far towards the dots being centred the whole moves (0: the whole centred, 1: the dots) */
const OPTICAL = 0.5

/** The design's arrangement, scaled to take a share of the screen's width
 *  (75% on a laptop, growing to 85% on large screens) and at most 78% of its
 *  height. Only the distances grow; the text keeps its size and its place
 *  beside its dot. (The whole is centred afterwards, text included.) */
function spread(vw: number, vh: number) {
    const L = WIDE
    const xs = L.dots.map((d) => d[0]),
        ys = L.dots.map((d) => d[1])
    const minX = Math.min(...xs),
        maxX = Math.max(...xs),
        minY = Math.min(...ys),
        maxY = Math.max(...ys)
    // what the text adds beyond the dots (px, unscaled)
    const right = Math.max(...L.dots.map((d, i) => d[0] - maxX + L.titles[i][0] + POINTS[i].width))
    const below = 48
    const above = 9
    const share = Math.min(0.85, Math.max(0.75, 0.75 + ((vw - 1440) / 1120) * 0.1))
    const scale = Math.min((share * vw - right) / (maxX - minX), (0.78 * vh - below - above) / (maxY - minY))
    return { scale, place: L.dots.map(([x, y]) => ({ x: (x - minX) * scale, y: (y - minY) * scale })) }
}

/** Short screens (a phone on its side): two columns, product design and brand
 *  on the left, delivery and creative leadership on the right, futurism in the
 *  very middle between them. */
function columns(vw: number, vh: number) {
    const side = 24
    const rowUp = Math.min(100, vh / 2 - 82),
        rowDown = Math.min(80, vh / 2 - 60)
    const right = vw / 2 + Math.min(60, vw * 0.06)
    return [
        // (a little lower than delivery: the line between them clears its title)
        { x: side + vw * 0.08, y: vh / 2 - rowUp + 30 },
        { x: right, y: vh / 2 - rowUp - 5 },
        { x: right - vw * 0.04, y: vh / 2 + rowDown },
        { x: side, y: vh / 2 + rowDown - 22 },
        { x: vw / 2 - vw * 0.06, y: vh / 2 },
    ]
}

/** Small or portrait screens: the four in a staggered column, futurism in the
 *  very middle, all within a band clear of the header (top) and the menu button
 *  (bottom on phones), each text with the room the screen leaves it. Order as
 *  in the design: product design, brand, futurism, delivery, creative
 *  leadership, top to bottom. */
const TALL = {
    // across: 0 the left edge, 1 as far right as leaves room for a text;
    // down: -1 the top of the band, 0 the middle, 1 its bottom
    // (product design and delivery on the right, measured in from the right
    // edge as a share of the width: their dots on the right, text to the left)
    at: [
        [0, -1],
        [0.1, 0.5],
        [0.02, 1],
        [0.06, -0.5],
        [0.55, 0],
    ],
    flip: [true, true, false, false, false],
    titles: [
        [14, -8.5],
        [14, -8.5],
        [14, -8.5],
        [14, -8.5],
        [14, -8.5],
    ],
}
function stacked(vw: number, vh: number) {
    // (a margin growing with the screen: wider screens, more air at the edges)
    const side = Math.max(16, vw * 0.07)
    const text = Math.min(200, vw * 0.45)
    const across = Math.max(0, vw - 2 * side - text - 14)
    // the band: under the header, over the menu button (and the last text)
    const up = Math.max(60, vh / 2 - 110)
    const down = Math.max(60, vh / 2 - 100 - 56)
    return TALL.at.map(([fx, fy], i) => ({
        // (on the right, their text to the left: in from the right edge)
        x: TALL.flip[i] ? vw - side - 4 - fx * vw : side + fx * across,
        y: vh / 2 + fy * (fy < 0 ? up : down),
    }))
}

const RING = 34.5
// the build (ms from when it starts)
// (one after another: each sets off this long after the one before)
const DOTS = { out: 100, run: 900, step: 170 }
export const TEXT_AT = 1500
const TEXT_STEP = 160
const LINES = { at: 2150, dur: 1500 }
// futurism: once the four texts are in (about 2.56s), while the line still goes round
const FUTURE = { dot: 2600, ring: 2660, text: 2900 }
/** the whole build is done (futurism's text in, the line round) */
export const BUILD_END = Math.max(FUTURE.text + 90 * 3 + 400, LINES.at + LINES.dur)

/** After it, scrolling on: the texts go and the lines undraw, and the four
 *  outer dots become the corners of a frame of logos (Figma "Frame 977", 23:35).
 *  Monochrome white SVGs (public/about/logos), left to right, top to bottom
 *  (Tiago, 5 Nov): each its size in the 739x230 box of the design (px), in the
 *  design's five columns (the outer ones against the box's sides, the others
 *  centred on them) and two rows (centred at 35 and 196). */
const COL = [null, 198.75, 366, 537.5, null]
const ROW = [35, 196]
const at = (id: string, col: number, row: number, w: number, h: number) => ({
    id,
    x: col === 0 ? 5 : col === 4 ? 736 - w : COL[col]! - w / 2,
    y: ROW[row] - h / 2,
    w,
    h,
})
const LOGOS = [
    at("inter", 0, 0, 49, 49),
    at("mancity", 1, 0, 49, 49),
    at("formula-e", 2, 0, 101, 15.7),
    at("redbull", 3, 0, 72, 35.7),
    at("lufthansa", 4, 0, 96, 16.4),
    at("klm", 0, 1, 55.3, 32.5),
    at("smart", 1, 1, 72, 18.6),
    at("samsung", 2, 1, 82, 12.5),
    at("bol", 3, 1, 56, 22),
    at("ns", 4, 1, 56, 20.9),
]
/** the frame: its corner dots' centres span 807x270; the logos' box starts
 *  29.5 in and 12.5 down from the top left one */
const FRAME = { w: 807, h: 270, bx: 29.5, by: 12.5 }
/** which corner each outer point goes to: product design top right, delivery
 *  bottom right, creative leadership bottom left, brand top left */
const CORNER = [
    [1, 0],
    [1, 1],
    [0, 1],
    [0, 0],
]
// the change (ms from when it starts; played backwards when scrolled back)
// (intertwined: the dots set off while the texts go, the logos come while
// the dots arrive)
const SWAP = { textStep: 50, lineStep: 35, textDur: 300, ring: 300, futureDot: [200, 250], undraw: [100, 550], move: [250, 850], logos: 700, logoStep: 50, logoDur: 450 }
/** on to the award: the logos out one after another, the dots pairing up beside
 *  its text (two of them joining the other two), then the text in, line by line */
const LEAVE = {
    logoStep: 45,
    logoDur: 360,
    // each pair: meeting first (halfway along their side of the frame), then
    // on together along a curve; the left one setting off earlier, arriving first
    pairs: { left: [150, 820], right: [320, 880] },
    text: 800,
    textStep: 130,
    textDur: 420,
    // (desktop: word by word from just after the award starts to show, the right
    // text a moment after the left)
    wordAt: 660,
    sideStep: 170, // (the right dots arrive later: their text with them, all in by 1250)
    wordStep: 30,
    wordDur: 300,
}
const LEAVE_END = 1300
/** the end: the award goes to black, its text going and its dots coming
 *  together, out of step and along a curve, as one between the contact's lines */
const CONTACT = ["Have a project in mind?", "tiago@nascimen.to"]
const EMAIL = "tiago@nascimen.to"
const END = {
    // the award's text going (outermost words first)
    textOut: 0,
    textStep: 35,
    textDur: 280,
    // the left dot to the middle (bowing up); the right one, from where it is,
    // drawing a circle round it (the pencil), clockwise, all the way round
    left: [250, 650],
    right: [400, 650],
    bow: { left: -0.26, right: -0.22 },
    draw: [350, 2500],
    // the contact's lines, word by word outwards from the dot
    contact: [520, 760],
    wordStep: 40,
    wordDur: 320,
}
/** the pencil's circle, in this many short arcs (each its own opacity: a trail) */
const HALO_SEGS = 120
const END_LENGTH = 3050 // (2200 a 2.6 screens: the same pace, the drawing longer)
/** the contact's lines from the dot (px): beside it, or (phones) above and below */
const CONTACT_GAP = { side: 92, stacked: 36 }
/** the award's text: two lines (left and right of it; stacked under it on phones) */
const AWARD_TEXT = ["Honoured to have been awarded best in design", "by the Dutch Interactive Awards"]
const SWAP_END = SWAP.logos + SWAP.logoStep * (LOGOS.length - 1) + SWAP.logoDur

type Box = { x: number; y: number; w: number; h: number }
/** The frame and its logos on this screen. The logos keep their own size on
 *  every screen; the frame and the spacing between them grow with it (the
 *  design's arrangement at up to 62% of the screen's width and 55% of its
 *  height). Below the scale where they'd crowd each other: two columns of five,
 *  the frame around them. Centred. */
function logoLayout(vw: number, vh: number): { frame: Box; logos: Box[] } {
    const k = Math.min((vw * 0.62) / FRAME.w, (vh * 0.55) / FRAME.h)
    if (vw / vh >= 0.9 && k >= 0.75) {
        const w = FRAME.w * k,
            h = FRAME.h * k
        const frame = { x: vw / 2 - w / 2, y: vh / 2 - h / 2, w, h }
        return {
            frame,
            // (each where the design has its centre, at its own size)
            logos: LOGOS.map((l) => {
                const cx = frame.x + (FRAME.bx + l.x + l.w / 2) * k,
                    cy = frame.y + (FRAME.by + l.y + l.h / 2) * k
                return { x: cx - l.w / 2, y: cy - l.h / 2, w: l.w, h: l.h }
            }),
        }
    }
    // two columns of five, each logo at its size, centred in its cell
    const side = Math.max(28, vw * 0.07)
    const w = Math.min(vw - 2 * side, 440)
    const rows = Math.ceil(LOGOS.length / 2)
    const row = Math.max(56, Math.min(84, (vh - 260) / rows))
    const h = rows * row + 24
    const frame = { x: vw / 2 - w / 2, y: vh / 2 - h / 2, w, h }
    // (only if a cell can't hold the widest)
    const ls = Math.min(1, (w / 2 - 24) / 101)
    return {
        frame,
        logos: LOGOS.map((l, i) => {
            const cx = frame.x + (w / 4) * (1 + 2 * (i % 2)),
                cy = frame.y + 12 + row * (Math.floor(i / 2) + 0.5)
            return { x: cx - (l.w * ls) / 2, y: cy - (l.h * ls) / 2, w: l.w * ls, h: l.h * ls }
        }),
    }
}

export type ConstellationApi = {
    /** as far as the scroll has come: the build (0..1), the change to the logos
     *  (0..1), and where it is through the constellation's part (for passing it by) */
    set: (build: number, swap: number, pass: number, leave?: number, end?: number) => void
}
/** scrolling by it: how far it moves (px, up as you scroll down) and turns (rad) over its part */
const PASS = { move: 60, turn: 0.085, mid: 0.72 }

export default function Constellation({ api }: { api: React.MutableRefObject<ConstellationApi | null> }) {
    const root = React.useRef<HTMLDivElement>(null)
    const nodes = React.useRef<(HTMLDivElement | null)[]>([])
    const path = React.useRef<SVGPathElement>(null)
    const ring = React.useRef<SVGCircleElement>(null)
    const logoEls = React.useRef<(HTMLImageElement | null)[]>([])
    const awardText = React.useRef<(HTMLElement | null)[]>([])
    const contactText = React.useRef<(HTMLElement | null)[]>([])
    const halo = React.useRef<SVGGElement>(null)

    React.useLayoutEffect(() => {
        const el = root.current!
        const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches
        const fine = matchMedia("(pointer: fine)").matches
        // each text's lines (however it wraps): its words, line by line
        let lines: { el: HTMLElement; line: number }[][] = []
        const measure = () => {
            lines = nodes.current.map((n) => {
                // (only the text shown on this screen: the long one or the phones' one)
                const words = Array.from(n!.querySelectorAll<HTMLElement>(".cn-w")).filter((w) => w.offsetParent)
                let top = -Infinity,
                    k = -1
                return words.map((w) => {
                    const t = w.offsetTop
                    if (Math.abs(t - top) > 2) ((top = t), k++)
                    return { el: w.firstElementChild as HTMLElement, line: k }
                })
            })
        }
        measure()
        ;(document as any).fonts?.ready?.then(() => layout())

        // the layout on this screen: each dot's place (px)
        let place: { x: number; y: number }[] = []
        let tall = false
        // how far the layout is scaled (the float and the pull grow a little with it)
        let scale = 1
        const layout = () => {
            const vw = innerWidth,
                vh = innerHeight
            // The spread arrangement (the design), as long as the screen leaves it
            // at least 80% of its size: below that the texts (always at their own
            // size) would run into each other, and the points stack instead.
            const wide = spread(vw, vh)
            tall = vw / vh < 0.9 || wide.scale < MIN_SPREAD
            el.toggleAttribute("data-tall", tall)
            const titles = tall ? TALL.titles : WIDE.titles
            if (tall) ((place = vh < 520 && vw > vh ? columns(vw, vh) : stacked(vw, vh)), (scale = 1))
            else ((place = wide.place), (scale = wide.scale))
            const low = tall && vh < 520 && vw > vh
            // (in the stacked column, product design and delivery sit on the right)
            const flip = (i: number) => tall && !low && TALL.flip[i]
            nodes.current.forEach((n, i) => {
                const [tx, ty] = titles[i]
                n!.toggleAttribute("data-flip", flip(i))
                n!.style.setProperty("--tx", tx + "px")
                n!.style.setProperty("--ty", ty + "px")
                // (no wider than the screen leaves it)
                // (and, low on a short screen, clear of the menu button in the corner)
                const room = flip(i) ? place[i].x - tx - 16 : vw - place[i].x - tx - 16 - (low && place[i].y > vh / 2 + 20 ? 90 : 0)
                n!.style.setProperty("--tw", Math.min(POINTS[i].width, Math.max(120, room)) + "px")
            })
            // the whole of it, text included, centred on the screen
            let l = Infinity,
                r = -Infinity,
                t = Infinity,
                b = -Infinity
            nodes.current.forEach((n, i) => {
                const tx = n!.querySelector<HTMLElement>(".cn-text")!
                const [ox, oy] = titles[i]
                const p = place[i]
                const w = tx.offsetWidth
                l = Math.min(l, p.x - 2, flip(i) ? p.x - ox - w : p.x + ox)
                r = Math.max(r, p.x + 2, flip(i) ? p.x - ox : p.x + ox + w)
                t = Math.min(t, p.y - 2, p.y + oy)
                b = Math.max(b, p.y + 2, p.y + oy + tx.offsetHeight)
            })
            let dx = (vw - (r - l)) / 2 - l
            const dy = (vh - (b - t)) / 2 - t
            // optically: the text hangs off to the right, so the dots read as off
            // to the left; move it part of the way towards the dots being centred
            // (not all of it, and never past the screen's edge)
            if (!tall) {
                const xs = place.map((p) => p.x)
                const dots = (Math.min(...xs) + Math.max(...xs)) / 2 + dx
                dx += Math.max(0, Math.min((vw / 2 - dots) * OPTICAL, vw - 24 - (r + dx)))
            }
            place = place.map((p) => ({ x: p.x + dx, y: p.y + dy }))
            measure()
            // the logos and the corners the dots go to
            const L = logoLayout(vw, vh)
            corners = CORNER.map(([cx, cy]) => ({ x: L.frame.x + cx * L.frame.w, y: L.frame.y + cy * L.frame.h }))
            // the award's text, and the two places the dots pair up at, beside it:
            // placed on the layer the award's video fills (the stage, a steady
            // 100vh: Safari's bars coming and going on phones don't move it)
            const sw = el.clientWidth || vw,
                sh = el.clientHeight || vh
            const tl = awardText.current[0]!,
                tr = awardText.current[1]!
            const gap = 22
            let left: { x: number; y: number }, right: { x: number; y: number }
            if (awardTall(sw, sh)) {
                // phones: stacked, between the award's foot and the screen's
                // bottom (clear of the menu button), the dots at the sides
                const s = Math.max(sw / AWARD_TALL.w, sh / AWARD_TALL.h)
                const foot = (sh - AWARD_TALL.h * s) / 2 + AWARD_FOOT * AWARD_TALL.h * s
                const y = (foot + (sh - 96)) / 2
                for (const [j, t] of [tl, tr].entries()) {
                    t.style.left = "50%"
                    t.style.right = "auto"
                    t.style.textAlign = "center"
                    t.style.transform = `translate(-50%, ${j === 0 ? "-100%" : "0"})`
                    t.style.top = y.toFixed(1) + "px"
                }
                const side = Math.max(16, sw * 0.067)
                left = { x: side, y }
                right = { x: sw - side, y }
            } else {
                // beside the award: one line on each side, at its middle
                const y = sh * 0.49
                tl.style.left = (sw * 0.128).toFixed(1) + "px"
                tl.style.right = "auto"
                tl.style.textAlign = "left"
                tl.style.transform = "translateY(-50%)"
                tl.style.top = y.toFixed(1) + "px"
                tr.style.left = "auto"
                tr.style.right = (sw * 0.136).toFixed(1) + "px"
                tr.style.textAlign = "right"
                tr.style.transform = "translateY(-50%)"
                tr.style.top = y.toFixed(1) + "px"
                left = { x: sw * 0.128 - gap, y }
                right = { x: sw - sw * 0.136 + gap, y }
            }
            // product design and delivery to the right; creative leadership and brand to the left
            awardCorners = [right, right, left, left]
            // the contact: its two lines either side of the dot, at the screen's
            // middle (phones: above and below it)
            contact = { x: sw / 2, y: sh / 2 }
            const [cq, ce] = contactText.current as HTMLElement[]
            if (awardTall(sw, sh)) {
                for (const [j, t] of [cq, ce].entries()) {
                    t.style.left = "50%"
                    t.style.right = "auto"
                    t.style.textAlign = "center"
                    t.style.top = (sh / 2 + (j === 0 ? -CONTACT_GAP.stacked : CONTACT_GAP.stacked)).toFixed(1) + "px"
                    t.style.transform = `translate(-50%, ${j === 0 ? "-100%" : "0"})`
                }
            } else {
                cq.style.left = "auto"
                cq.style.right = (sw / 2 + CONTACT_GAP.side).toFixed(1) + "px"
                ce.style.left = (sw / 2 + CONTACT_GAP.side).toFixed(1) + "px"
                ce.style.right = "auto"
                for (const t of [cq, ce]) ((t.style.top = (sh / 2).toFixed(1) + "px"), (t.style.transform = "translateY(-50%)"), (t.style.textAlign = "left"))
            }
            logoBox = L.logos
            logoEls.current.forEach((im, j) => {
                im!.style.width = L.logos[j].w.toFixed(1) + "px"
                im!.style.height = L.logos[j].h.toFixed(1) + "px"
            })
        }
        let corners: { x: number; y: number }[] = []
        let awardCorners: { x: number; y: number }[] = []
        let contact = { x: 0, y: 0 }
        let logoBox: Box[] = []
        layout()
        // What's drawn glides to the layout when the screen changes size (Safari's
        // bars coming and going on phones, a turn of the phone): eased copies.
        type P = { x: number; y: number }
        const copy = () => ({
            place: place.map((p) => ({ ...p })),
            corners: corners.map((p) => ({ ...p })),
            awardCorners: awardCorners.map((p) => ({ ...p })),
            contact: { ...contact },
            logos: logoBox.map((b) => ({ ...b })),
            centre: { x: innerWidth / 2, y: innerHeight / 2 },
        })
        let eased = copy()
        const glide = (dt: number) => {
            const k = 1 - Math.exp(-dt * 7)
            const to = (a: P, b: P) => ((a.x += (b.x - a.x) * k), (a.y += (b.y - a.y) * k))
            eased.place.forEach((p, i) => to(p, place[i]))
            eased.corners.forEach((p, i) => to(p, corners[i]))
            eased.awardCorners.forEach((p, i) => to(p, awardCorners[i]))
            to(eased.contact, contact)
            eased.logos.forEach((b, i) => (to(b, logoBox[i]), (b.w = logoBox[i].w), (b.h = logoBox[i].h)))
            to(eased.centre, { x: innerWidth / 2, y: innerHeight / 2 })
        }

        // the mouse, for the pull
        const mouse = { x: -1e4, y: -1e4 }
        const onMove = (e: PointerEvent) => ((mouse.x = e.clientX), (mouse.y = e.clientY))
        const onLeave = () => ((mouse.x = -1e4), (mouse.y = -1e4))
        if (fine) {
            addEventListener("pointermove", onMove, { passive: true })
            document.addEventListener("pointerleave", onLeave)
        }
        const pull = POINTS.map(() => ({ x: 0, y: 0 }))
        // each point's own float: slow, out of step with the others
        const drift = POINTS.map((_, i) => ({ px: 7.3 + i * 1.37, py: 9.1 + i * 0.83, ax: (i * 2.1) % 6.28, ay: (i * 3.7) % 6.28 }))

        // Where the scroll is: the build and then the change to the logos, as one
        // timeline (ms), controlled by the scroll; what's shown follows it with a
        // short smoothing (about a tenth of a second), so a flung scroll's jumps
        // don't show as jumps.
        // on to the award (ms into it, as the scroll has come)
        let leaving = 0
        // and on to the end (ms into it)
        let ending = 0
        let goal = 0,
            shownT = 0,
            t = 0,
            u = 0
        const logoPull = LOGOS.map(() => ({ x: 0, y: 0 }))
        // the pull towards the mouse: how far a point at p leans (px)
        const lean = (p: { x: number; y: number }, R: number, strength: number) => {
            const dx = mouse.x - p.x,
                dy = mouse.y - p.y
            const dist = Math.hypot(dx, dy)
            if (dist >= R || dist < 0.1) return { x: 0, y: 0 }
            const k = (1 - dist / R) ** 2 * strength
            return { x: dx * k, y: dy * k }
        }
        // the scroll through its part, followed with a little delay (as if
        // passing it by)
        let passTo = 0.5,
            pass = 0.5
        let raf = 0,
            last = 0,
            visible = false
        const frame = (now: number) => {
            raf = 0
            const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60
            last = now
            shownT += (goal - shownT) * (1 - Math.exp(-dt * 11))
            if (reduced || Math.abs(goal - shownT) < 1) shownT = goal
            t = Math.min(shownT, BUILD_END)
            u = Math.max(0, shownT - BUILD_END)
            if (t <= 0) {
                // (not yet: nothing on screen, nothing running)
                el.style.visibility = "hidden"
                visible = false
                last = 0
                return
            }
            if (!visible) ((visible = true), (eased = copy()))
            el.style.visibility = "visible"
            const secs = now / 1000
            glide(dt)
            const move = progress(u, SWAP.move[0], SWAP.move[1], "smooth")
            // on to the award: each pair of dots meets, then goes on together
            // along a curve to its place beside the award's text
            const pairOf = (i: number) => (i <= 1 ? [0, 1] : [2, 3])
            const toAward = (i: number, from: { x: number; y: number }) => {
                const [a, b] = pairOf(i)
                const [start, dur] = i <= 1 ? LEAVE.pairs.right : LEAVE.pairs.left
                const meet = progress(leaving, start, dur * 0.42, "smooth")
                const go = progress(leaving, start + dur * 0.36, dur * 0.64, "smooth")
                if (meet <= 0) return from
                const ca = eased.corners[a],
                    cb = eased.corners[b]
                // (phones: the upper dot just comes down to the lower one)
                const m = tall ? eased.corners[i <= 1 ? 1 : 2] : { x: (ca.x + cb.x) / 2, y: (ca.y + cb.y) / 2 }
                const f = eased.awardCorners[i]
                // (bowing: up on the left, down on the right)
                const dx = f.x - m.x,
                    dy = f.y - m.y
                const bow = tall ? 0 : (i <= 1 ? 1 : -1) * 0.28 // (phones: straight)
                const k = { x: (m.x + f.x) / 2 - dy * bow, y: (m.y + f.y) / 2 + dx * bow }
                const q = 1 - go
                const curve = { x: q * q * m.x + 2 * q * go * k.x + go * go * f.x, y: q * q * m.y + 2 * q * go * k.y + go * go * f.y }
                const base = { x: from.x + (m.x - from.x) * meet, y: from.y + (m.y - from.y) * meet }
                return { x: base.x + curve.x - m.x, y: base.y + curve.y - m.y }
            }
            // the end: each side's dot on to the contact's, along its own curve, the
            // left first; there they're one
            // (the pencil: the circle through where the right dot is, round the
            // middle, drawn from there clockwise all the way round; past the
            // screen's edges)
            const drawn = progress(ending, END.draw[0], END.draw[1], "smooth")
            const pen0 = eased.awardCorners[0]
            const haloR = Math.hypot(pen0.x - eased.contact.x, pen0.y - eased.contact.y)
            const haloAt = Math.atan2(pen0.y - eased.contact.y, pen0.x - eased.contact.x)
            const onHalo = (a: number) => ({ x: eased.contact.x + haloR * Math.cos(haloAt + a), y: eased.contact.y + haloR * Math.sin(haloAt + a) })
            const toEnd = (i: number, from: { x: number; y: number }) => {
                const side = i <= 1 ? "right" : "left"
                if (side === "right") return drawn > 0 ? onHalo(drawn * Math.PI * 2) : from
                const [start, dur] = END[side]
                const g = progress(ending, start, dur, "smooth")
                if (g <= 0) return from
                const f = eased.contact
                const dx = f.x - from.x,
                    dy = f.y - from.y
                const bow = END.bow[side]
                const k = { x: (from.x + f.x) / 2 - dy * bow, y: (from.y + f.y) / 2 + dx * bow }
                const q = 1 - g
                return { x: q * q * from.x + 2 * q * g * k.x + g * g * f.x, y: q * q * from.y + 2 * q * g * k.y + g * g * f.y }
            }
            pass += (passTo - pass) * (1 - Math.exp(-dt * 14))
            // scrolling by: it drifts up and turns a little about the screen's
            // centre (the texts stay upright), less and less as the logos come
            // (its middle where it's seen whole, from a little below to a little above)
            const by = (PASS.mid - Math.max(-0.3, Math.min(1.3, pass))) * (1 - move)
            // (turning clockwise as you scroll on: towards the level frame of the logos)
            const turn0 = -by * PASS.turn,
                cos = Math.cos(turn0),
                sin = Math.sin(turn0)
            const ox = eased.centre.x,
                oy = eased.centre.y
            const passed = (p: { x: number; y: number }) => {
                const x = p.x - ox,
                    y = p.y - oy
                return { x: ox + x * cos - y * sin, y: oy + x * sin + y * cos + by * PASS.move }
            }

            // where each dot is: its place, floating, pulled by the mouse
            const float = reduced ? 0 : progress(t, 800, 2900, "smooth")
            const at = eased.place.map((pp, i) => {
                const p0 = passed(pp)
                const d = drift[i]
                const amp = Math.sqrt(Math.max(1, scale)) * (1 - move)
                const fx = Math.sin((secs * 6.283) / d.px + d.ax) * 6 * amp * float
                const fy = Math.sin((secs * 6.283) / d.py + d.ay) * 5 * amp * float
                // (the outer four on their way to the frame's corners)
                const c = i === CENTRE ? p0 : toEnd(i, toAward(i, eased.corners[i]))
                const p = { x: p0.x + (c.x - p0.x) * move + fx, y: p0.y + (c.y - p0.y) * move + fy }
                const m = lean(p, 240 * Math.sqrt(Math.max(1, scale)), 0.22)
                const q = pull[i]
                q.x += (m.x - q.x) * (1 - Math.exp(-dt * 5))
                q.y += (m.y - q.y) * (1 - Math.exp(-dt * 5))
                return { x: p.x + q.x, y: p.y + q.y }
            })

            // the outer four appear at the screen's centre and go out to their
            // places (simple, so it reads the same scrolled fast or back)
            const c = eased.centre
            // (in sequence, 1 2 3 4; each with a slight spiral, unwinding into place)
            const outOf = (i: number) => progress(t, DOTS.out + DOTS.step * i, DOTS.run, "smooth")
            const shownAt = at.map((p, i) => {
                if (i === CENTRE) return p
                const out = outOf(i)
                const swirl = -0.6 * (1 - out),
                    sc = Math.cos(swirl),
                    sn = Math.sin(swirl)
                const x = (p.x - c.x) * out,
                    y = (p.y - c.y) * out
                return { x: c.x + x * sc - y * sn, y: c.y + x * sn + y * sc }
            })

            nodes.current.forEach((n, i) => {
                const p = shownAt[i]
                n!.style.transform = `translate3d(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px, 0)`
                const dot = n!.firstElementChild as HTMLElement
                if (i === CENTRE) {
                    const k = progress(t, FUTURE.dot, 420, "out") * (1 - progress(u, SWAP.futureDot[0], SWAP.futureDot[1], "linear"))
                    dot.style.opacity = String(k)
                    dot.style.transform = `scale(${(0.2 + 0.8 * k).toFixed(3)})`
                } else dot.style.opacity = String(progress(t, DOTS.step * i, 260, "linear"))
                // the text, line by line (as the home headline: rising, 90ms apart)
                const start = i === CENTRE ? FUTURE.text : TEXT_AT + i * TEXT_STEP
                // and going, line by line, rising away (futurism with the first)
                const order = i === CENTRE ? 0 : i + 1
                for (const w of lines[i] || []) {
                    const k = progress(t, start + 90 * w.line, 400, "out")
                    const o = progress(u, SWAP.textStep * order + SWAP.lineStep * w.line, SWAP.textDur, "natural")
                    const y = 100 * (1 - k) - 70 * o
                    w.el.style.transform = y !== 0 ? `translateY(${y.toFixed(1)}%)` : ""
                    w.el.style.opacity = String(k * (1 - o))
                }
            })

            // the lines between the outer four, drawn round (and undrawn)
            const d = LOOP.map((i, k) => `${k ? "L" : "M"}${shownAt[i].x.toFixed(1)} ${shownAt[i].y.toFixed(1)}`).join(" ") + " Z"
            path.current!.setAttribute("d", d)
            const draw = progress(t, LINES.at, LINES.dur, "smooth") * (1 - progress(u, SWAP.undraw[0], SWAP.undraw[1], "smooth"))
            path.current!.style.strokeDashoffset = (1 - draw).toFixed(4)
            path.current!.style.opacity = draw > 0 ? "1" : "0"
            // futurism's ring, opening around it
            const r = progress(t, FUTURE.ring, 1000, "opening")
            // the pencil's circle, as far as it's drawn
            // a trail, in short arcs: full at the pencil, nothing where it started,
            // evenly between (the fade growing as the line does)
            const segs = halo.current!.children
            for (let k = 0; k < HALO_SEGS; k++) {
                const seg = segs[k] as SVGPathElement
                const a0 = k / HALO_SEGS
                if (a0 >= drawn) {
                    if (seg.style.display !== "none") seg.style.display = "none"
                    continue
                }
                const a1 = Math.min(drawn, (k + 1) / HALO_SEGS)
                const p0 = onHalo(a0 * Math.PI * 2),
                    p1 = onHalo(a1 * Math.PI * 2)
                seg.setAttribute("d", `M${p0.x.toFixed(1)} ${p0.y.toFixed(1)}A${haloR.toFixed(1)} ${haloR.toFixed(1)} 0 0 1 ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`)
                seg.style.opacity = ((a0 + a1) / 2 / drawn).toFixed(3)
                seg.style.display = ""
            }
            const rc = ring.current!
            rc.setAttribute("cx", at[CENTRE].x.toFixed(1))
            rc.setAttribute("cy", at[CENTRE].y.toFixed(1))
            rc.setAttribute("r", (RING * (0.35 + 0.65 * r)).toFixed(2))
            rc.style.opacity = String(progress(t, FUTURE.ring, 600, "linear") * (1 - progress(u, 0, SWAP.ring, "linear")))

            // the logos, one after another, rising in; leaning to the mouse
            logoEls.current.forEach((im, j) => {
                // (in one after another; and out the same way as the award comes)
                const k =
                    progress(u, SWAP.logos + SWAP.logoStep * j, SWAP.logoDur, "out") *
                    (1 - progress(leaving, LEAVE.logoStep * j, LEAVE.logoDur, "natural"))
                const b = eased.logos[j]
                const q = logoPull[j]
                const m = k > 0 ? lean({ x: b.x + b.w / 2, y: b.y + b.h / 2 }, 160, 0.16) : { x: 0, y: 0 }
                q.x += (m.x - q.x) * (1 - Math.exp(-dt * 5))
                q.y += (m.y - q.y) * (1 - Math.exp(-dt * 5))
                im!.style.opacity = k.toFixed(3)
                im!.style.visibility = k > 0 ? "visible" : "hidden"
                im!.style.transform = `translate3d(${(b.x + q.x).toFixed(1)}px, ${(b.y + q.y + 14 * (1 - k)).toFixed(1)}px, 0)`
            })

            // the award's text rising in: on desktop word by word, outwards from
            // the award (the left text from its right end, the right one from its
            // left); on phones, stacked, line by line
            awardText.current.forEach((el, j) => {
                const words = Array.from(el!.children) as HTMLElement[]
                let any = false
                words.forEach((span, w) => {
                    const order = j === 0 ? words.length - 1 - w : w
                    const k = tall
                        ? progress(leaving, LEAVE.text + LEAVE.textStep * j, LEAVE.textDur, "out")
                        : progress(leaving, LEAVE.wordAt + LEAVE.sideStep * j + LEAVE.wordStep * order, LEAVE.wordDur, "out")
                    // (and at the end going, rising away, the outermost words first)
                    const o = progress(ending, END.textOut + END.textStep * (words.length - 1 - order), END.textDur, "natural")
                    const y = 100 * (1 - k) - 70 * o
                    span.style.opacity = (k * (1 - o)).toFixed(3)
                    span.style.transform = y !== 0 ? `translateY(${y.toFixed(1)}%)` : ""
                    any ||= k * (1 - o) > 0
                })
                el!.style.visibility = any ? "visible" : "hidden"
            })

            // the contact's lines, word by word outwards from the dot (phones:
            // in reading order), each done as its side's dot arrives
            contactText.current.forEach((el, j) => {
                const words = Array.from(el!.querySelectorAll("span")) as HTMLElement[]
                let any = false
                words.forEach((span, w) => {
                    const order = j === 0 && !tall ? words.length - 1 - w : w
                    const k = progress(ending, END.contact[j] + END.wordStep * order, END.wordDur, "out")
                    span.style.opacity = k.toFixed(3)
                    span.style.transform = k < 1 ? `translateY(${(100 * (1 - k)).toFixed(1)}%)` : ""
                    any ||= k > 0
                })
                el!.style.visibility = any ? "visible" : "hidden"
                el!.toggleAttribute("data-on", any)
            })

            // (on while it's on screen: it floats, and leans to the mouse)
            raf = requestAnimationFrame(frame)
        }
        const kick = () => raf || (raf = requestAnimationFrame(frame))
        api.current = {
            set(build, swap, through, leave = 0, end = 0) {
                kick()
                leaving = leave * LEAVE_END
                ending = end * END_LENGTH
                goal = build * BUILD_END + (build >= 0.9999 ? swap * SWAP_END : 0)
                passTo = through
                kick()
            },
        }
        const onResize = () => layout()
        addEventListener("resize", onResize)
        return () => {
            cancelAnimationFrame(raf)
            api.current = null
            removeEventListener("resize", onResize)
            removeEventListener("pointermove", onMove)
            document.removeEventListener("pointerleave", onLeave)
        }
    }, [])

    return (
        <div ref={root} className="cn" aria-label="What I do" style={{ visibility: "hidden" }}>
            <svg className="cn-lines" aria-hidden="true">
                <path ref={path} pathLength={1} />
                <circle ref={ring} r={RING} />
                <g ref={halo} className="cn-halo">
                    {Array.from({ length: HALO_SEGS }, (_, k) => (
                        <path key={k} style={{ display: "none" }} />
                    ))}
                </g>
            </svg>
            {AWARD_TEXT.map((line, j) => (
                <p key={j} ref={(el) => (awardText.current[j] = el)} className="cn-award">
                    {line.split(" ").map((w, k) => (
                        <React.Fragment key={k}>
                            {k > 0 && " "}
                            <span>{w}</span>
                        </React.Fragment>
                    ))}
                </p>
            ))}
            {CONTACT.map((line, j) => (
                <p key={"c" + j} ref={(el) => (contactText.current[j] = el)} className="cn-award cn-contact">
                    {j === 1 ? (
                        <a href={`mailto:${EMAIL}`}>
                            <span>{line}</span>
                        </a>
                    ) : (
                        line.split(" ").map((w, k) => (
                            <React.Fragment key={k}>
                                {k > 0 && " "}
                                <span>{w}</span>
                            </React.Fragment>
                        ))
                    )}
                </p>
            ))}
            <div className="cn-logos" aria-label="Clients">
                {LOGOS.map((l, j) => (
                    <img key={l.id} ref={(el) => (logoEls.current[j] = el)} className="cn-logo" src={`/about/logos/${l.id}.svg?v=2`} alt={l.id} decoding="async" />
                ))}
            </div>
            {POINTS.map((p, i) => (
                <div key={p.id} ref={(el) => (nodes.current[i] = el)} className={"cn-node" + (i === CENTRE ? " cn-centre" : "")}>
                    <i className="cn-dot" />
                    <div className="cn-text">
                        <h3 className="cn-title">
                            <Words text={p.title} />
                        </h3>
                        <p className="cn-body cn-long">
                            <Words text={p.text} />
                        </p>
                        <p className="cn-body cn-short">
                            <Words text={p.short} />
                        </p>
                    </div>
                </div>
            ))}
        </div>
    )
}

function Words({ text }: { text: string }) {
    return (
        <>
            {text.split(" ").map((w, i) => (
                <React.Fragment key={i}>
                    {i > 0 && " "}
                    <span className="cn-w">
                        <span>{w}</span>
                    </span>
                </React.Fragment>
            ))}
        </>
    )
}
